import {
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    FormControlLabel,
    MenuItem,
    Switch,
    TextField,
    Typography,
} from "@material-ui/core"
import { useSnackbar } from "notistack"
import React, { useEffect, useMemo, useState } from "react"
import { getPdvozConfig, salvarPdvozConfig } from "../../services/pdvoz"

/**
 * Editor das CHAVES DE IA do PDVoz (OpenAI, Anthropic, ElevenLabs) de um
 * estabelecimento. É só isso que mora no servidor: o resto da configuração do
 * app de voz (cozinha, totem, wake, pagamento, aparência…) fica LOCAL no
 * aparelho, na tela de Configurações da PWA. A PWA busca estas chaves no login.
 *
 * Form dirigido por schema: cada campo é um item de CAMPOS; renderCampo desenha
 * conforme o tipo. Assim, somar uma chave é uma linha no schema, não um bloco JSX.
 */

type Tipo = "text" | "password" | "number" | "bool" | "select" | "textarea" | "csv" | "color"

type Campo = {
    key: string
    label: string
    tipo: Tipo
    secao: string
    def: any
    opcoes?: { valor: string; rotulo: string }[]
    hint?: string
}

const SECOES = [
    "Chaves de IA",
]

/* Só as 3 chaves de IA moram no servidor (por estabelecimento). O RESTO da
   configuração do PDVoz (cozinha, totem, wake, pagamento, aparência…) fica LOCAL
   no aparelho, na tela de Configurações da PWA — porque ainda muda toda hora e
   subir cada parâmetro novo exigiria mexer no backend/admin. A PWA busca só estas
   chaves no login e as usa no lugar das digitadas à mão. */
const CAMPOS: Campo[] = [
    { key: "openaiApiKey", label: "Chave OpenAI (voz e áudio)", tipo: "password", secao: "Chaves de IA", def: "", hint: "Transcreve a fala (Whisper) e fala os pedidos na cozinha (TTS)." },
    { key: "anthropicApiKey", label: "Chave Anthropic (interpretação)", tipo: "password", secao: "Chaves de IA", def: "", hint: "Interpreta o pedido falado e monta os itens do cardápio (Claude)." },
    { key: "elevenlabsApiKey", label: "Chave ElevenLabs (opcional)", tipo: "password", secao: "Chaves de IA", def: "", hint: "Só quando o modelo de transcrição escolhido no aparelho é um Scribe." },
]

function defaultsDoSchema(): Record<string, any> {
    const o: Record<string, any> = {}
    for (const c of CAMPOS) o[c.key] = c.def
    return o
}

/** Converte os campos numéricos pra number antes de salvar (o input entrega string). */
function sanitizar(valores: Record<string, any>): Record<string, any> {
    const out = { ...valores }
    for (const c of CAMPOS) {
        if (c.tipo === "number") {
            const v = out[c.key]
            if (v === "" || v === null || v === undefined) {
                out[c.key] = c.def
            } else {
                const n = Number(String(v).replace(",", "."))
                out[c.key] = Number.isFinite(n) ? n : c.def
            }
        }
    }
    return out
}

function renderCampo(c: Campo, valor: any, set: (k: string, v: any) => void) {
    if (c.tipo === "bool") {
        return (
            <div key={c.key} style={{ margin: "4px 0" }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={!!valor}
                            color="primary"
                            onChange={e => set(c.key, e.target.checked)}
                        />
                    }
                    label={c.label}
                />
                {c.hint && (
                    <Typography variant="caption" display="block" color="textSecondary" style={{ marginLeft: 46, marginTop: -4 }}>
                        {c.hint}
                    </Typography>
                )}
            </div>
        )
    }
    if (c.tipo === "select") {
        return (
            <TextField
                key={c.key}
                select
                fullWidth
                margin="dense"
                variant="outlined"
                label={c.label}
                value={valor ?? ""}
                helperText={c.hint}
                onChange={e => set(c.key, e.target.value)}
            >
                {(c.opcoes || []).map(o => (
                    <MenuItem key={o.valor} value={o.valor}>
                        {o.rotulo}
                    </MenuItem>
                ))}
            </TextField>
        )
    }
    if (c.tipo === "csv") {
        const txt = Array.isArray(valor) ? valor.join(", ") : valor ?? ""
        return (
            <TextField
                key={c.key}
                fullWidth
                margin="dense"
                variant="outlined"
                label={c.label}
                value={txt}
                helperText={c.hint}
                onChange={e =>
                    set(
                        c.key,
                        e.target.value
                            .split(",")
                            .map(s => s.trim())
                            .filter(Boolean)
                    )
                }
            />
        )
    }
    const htmlType =
        c.tipo === "password" ? "password" : c.tipo === "number" ? "number" : c.tipo === "color" ? "color" : "text"
    return (
        <TextField
            key={c.key}
            fullWidth
            margin="dense"
            variant="outlined"
            label={c.label}
            type={htmlType}
            multiline={c.tipo === "textarea"}
            rows={c.tipo === "textarea" ? 3 : undefined}
            InputLabelProps={c.tipo === "color" ? { shrink: true } : undefined}
            value={valor ?? ""}
            helperText={c.hint}
            onChange={e => set(c.key, e.target.value)}
        />
    )
}

export function PDVozEditor({ idEstabelecimento }: { idEstabelecimento: number | string }) {
    const { enqueueSnackbar } = useSnackbar()
    const defaults = useMemo(() => defaultsDoSchema(), [])
    const [valores, setValores] = useState<Record<string, any>>(defaults)
    const [carregando, setCarregando] = useState(true)
    const [salvando, setSalvando] = useState(false)

    useEffect(() => {
        let vivo = true
        setCarregando(true)
        getPdvozConfig(idEstabelecimento)
            .then(cfg => {
                if (vivo) setValores({ ...defaults, ...(cfg || {}) })
            })
            .catch(e =>
                enqueueSnackbar("Não consegui carregar a configuração: " + (e?.message || e), { variant: "error" })
            )
            .finally(() => {
                if (vivo) setCarregando(false)
            })
        return () => {
            vivo = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idEstabelecimento])

    const set = (k: string, v: any) => setValores(prev => ({ ...prev, [k]: v }))

    const salvar = async () => {
        setSalvando(true)
        try {
            await salvarPdvozConfig(sanitizar(valores), idEstabelecimento)
            enqueueSnackbar("Configuração do PDVoz salva.", { variant: "success" })
        } catch (e: any) {
            enqueueSnackbar(
                "Não consegui salvar: " + (e?.response?.data?.mensagem || e?.message || e),
                { variant: "error" }
            )
        } finally {
            setSalvando(false)
        }
    }

    if (carregando) {
        return (
            <div style={{ padding: 48, textAlign: "center" }}>
                <CircularProgress />
            </div>
        )
    }

    return (
        <div style={{ maxWidth: 860, margin: "0 auto", paddingBottom: 80 }}>
            <Typography variant="body2" color="textSecondary" style={{ margin: "8px 0 16px" }}>
                Só as chaves de IA deste estabelecimento moram no servidor — a PWA as busca no login.
                O resto da configuração (cozinha, totem, wake, pagamento, aparência…) fica local no aparelho.
            </Typography>

            {SECOES.map(secao => (
                <Card key={secao} style={{ marginBottom: 16 }}>
                    <CardContent>
                        <Typography variant="h6">{secao}</Typography>
                        <Divider style={{ margin: "8px 0 12px" }} />
                        {CAMPOS.filter(c => c.secao === secao).map(c =>
                            renderCampo(c, valores[c.key], set)
                        )}
                    </CardContent>
                </Card>
            ))}

            <div
                style={{
                    position: "sticky",
                    bottom: 0,
                    background: "#eceff1",
                    padding: "12px 0",
                    textAlign: "right",
                }}
            >
                <Button variant="contained" color="primary" onClick={salvar} disabled={salvando}>
                    {salvando ? "Salvando…" : "Salvar configuração"}
                </Button>
            </div>
        </div>
    )
}
